// Shared dialogs for the "On Site" sign-in/sign-out feature — used by both
// the aggregate OnSitePage (full estate view) and the per-project embedded
// panel on the client portal's project page.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toLocalInput, personTypeLabel } from "@/lib/onsiteFormat";

const BRAND = "var(--brand-accent, #C75A1B)";

export function ManualSignInDialog({ clientId, sites = [], projects = [], defaultSiteId, defaultProjectId, hideLocationFields = false, onClose, onDone }) {
    const [siteId, setSiteId] = useState(defaultSiteId || "");
    const [projectId, setProjectId] = useState(defaultProjectId || "");
    const [contacts, setContacts] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [personTypes, setPersonTypes] = useState([]);
    const [personType, setPersonType] = useState("");
    const [personTypeOther, setPersonTypeOther] = useState("");
    const [contactId, setContactId] = useState("");
    const [mode, setMode] = useState("pick"); // pick | other
    const [form, setForm] = useState({ name: "", company: "", mobile: "", email: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const p = new URLSearchParams({ client_id: clientId });
        if (projectId) p.set("project_id", projectId);
        api.get(`/site-forms/attendance/people?${p.toString()}`)
            .then((r) => { setContacts(r.data?.contacts || []); setCompanies(r.data?.companies || []); setPersonTypes(r.data?.person_types || []); })
            .catch(() => { setContacts([]); setCompanies([]); setPersonTypes([]); });
    }, [clientId, projectId]);

    const lookupPersonType = async (email, mobile) => {
        if (!email && !mobile) return;
        try {
            const p = new URLSearchParams({ client_id: clientId });
            if (email) p.set("email", email);
            if (mobile) p.set("mobile", mobile);
            const r = await api.get(`/site-forms/attendance/lookup?${p.toString()}`);
            if (r.data?.person_type) setPersonType(r.data.person_type);
            if (r.data?.person_type_other) setPersonTypeOther(r.data.person_type_other);
        } catch { /* best-effort prefill, ignore failures */ }
    };

    const pickContact = (id) => {
        setContactId(id);
        const c = contacts.find((x) => x.id === id);
        if (c) {
            setForm({ name: c.name || "", company: form.company, mobile: c.mobile || "", email: c.email || "" });
            lookupPersonType(c.email, c.mobile);
        }
    };

    const submit = async () => {
        if (mode === "pick" && !contactId) { toast.error("Pick a person, or switch to \"Someone else\"."); return; }
        if (mode === "other" && !form.name.trim()) { toast.error("Enter a name."); return; }
        if (!personType) { toast.error("Select who this is signing in."); return; }
        if (personType === "Subcontractor" && !form.company.trim()) { toast.error("Company is required for a subcontractor."); return; }
        if (personType === "Other" && !personTypeOther.trim()) { toast.error("Please specify who this is."); return; }
        setSaving(true);
        try {
            await api.post("/site-forms/attendance/manual", {
                client_id: clientId, site_id: siteId || null, project_id: projectId || null,
                contact_id: mode === "pick" ? contactId : null,
                name: form.name || undefined, company: form.company || undefined,
                mobile: form.mobile || undefined, email: form.email || undefined,
                person_type: personType, person_type_other: personType === "Other" ? personTypeOther : undefined,
            });
            toast.success("Signed in.");
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not sign them in.");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="onsite-manual-signin-dialog" onClick={onClose}>
            <div className="bg-white rounded-[14px] w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold flex-1">Sign someone in</h3>
                    <button data-testid="onsite-manual-signin-close" onClick={onClose}><X className="w-4 h-4 text-[#9A968F]" /></button>
                </div>
                <div className="p-5 space-y-3.5">
                    {!hideLocationFields && (
                        <div className="grid grid-cols-2 gap-3">
                            <label className="text-[12px] font-semibold text-[#1F1F1F]">Site
                                <select data-testid="onsite-signin-site" value={siteId} onChange={(e) => setSiteId(e.target.value)} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                    <option value="">—</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </label>
                            <label className="text-[12px] font-semibold text-[#1F1F1F]">Project
                                <select data-testid="onsite-signin-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                    <option value="">—</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </label>
                        </div>
                    )}

                    <div className="flex gap-1 border-b" style={{ borderColor: "#E5E0DA" }}>
                        {[["pick", "Pick a known contact"], ["other", "Someone else"]].map(([id, label]) => (
                            <button key={id} data-testid={`onsite-signin-mode-${id}`} onClick={() => setMode(id)}
                                className="px-3 py-2 text-[12px] font-semibold border-b-2 -mb-px"
                                style={mode === id ? { borderColor: BRAND, color: BRAND } : { borderColor: "transparent", color: "#6F6A64" }}>{label}</button>
                        ))}
                    </div>

                    {mode === "pick" ? (
                        <select data-testid="onsite-signin-contact" value={contactId} onChange={(e) => pickContact(e.target.value)}
                            className="w-full h-9 px-2.5 rounded-lg border text-[13px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                            <option value="">— Select a person —</option>
                            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ""}</option>)}
                        </select>
                    ) : (
                        <label className="block text-[12px] font-semibold text-[#1F1F1F]">Name
                            <input data-testid="onsite-signin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                    )}

                    <label className="block text-[12px] font-semibold text-[#1F1F1F]">Who is signing in? <span className="text-red-500">*</span>
                        <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="onsite-signin-persontype">
                            {personTypes.map((pt) => (
                                <button key={pt} type="button" data-testid={`onsite-signin-persontype-${pt.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setPersonType(pt)}
                                    className="h-9 rounded-lg border text-[12.5px] font-semibold"
                                    style={personType === pt ? { background: BRAND, color: "#fff", borderColor: BRAND } : { borderColor: "#E5E0DA", color: "#1F1F1F", background: "#fff" }}>
                                    {personTypeLabel(pt)}
                                </button>
                            ))}
                        </div>
                    </label>
                    {personType === "Other" && (
                        <label className="block text-[12px] font-semibold text-[#1F1F1F]">Please specify <span className="text-red-500">*</span>
                            <input data-testid="onsite-signin-persontype-other" placeholder="e.g. Saltash staff, Trainee, Apprentice"
                                value={personTypeOther} onChange={(e) => setPersonTypeOther(e.target.value)} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                    )}
                    <label className="block text-[12px] font-semibold text-[#1F1F1F]">Company{personType === "Subcontractor" && <span className="text-red-500"> *</span>}
                        <input data-testid="onsite-signin-company" list="onsite-companies" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        <datalist id="onsite-companies">{companies.map((c) => <option key={c} value={c} />)}</datalist>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="text-[12px] font-semibold text-[#1F1F1F]">Mobile
                            <input data-testid="onsite-signin-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} onBlur={() => lookupPersonType(form.email, form.mobile)} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                        <label className="text-[12px] font-semibold text-[#1F1F1F]">Email <span className="text-[#9A968F] font-normal">(optional)</span>
                            <input data-testid="onsite-signin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={() => lookupPersonType(form.email, form.mobile)} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                    </div>
                    <p className="text-[11px] text-[#9A968F]">With an email, they get a one-tap sign-out link — handy for a big site where walking back to the poster isn't practical.</p>
                </div>
                <div className="px-5 py-3.5 border-t flex justify-end gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="onsite-manual-signin-cancel" onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="onsite-manual-signin-submit" onClick={submit} disabled={saving} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: BRAND }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : "Sign in"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function SignOutDialog({ rec, onClose, onDone }) {
    const [time, setTime] = useState(toLocalInput(rec.time_out));
    const [saving, setSaving] = useState(false);
    const correcting = !!rec.time_out;

    const submit = async () => {
        setSaving(true);
        try {
            await api.patch(`/site-forms/attendance/${rec.id}`, { time_out: new Date(time).toISOString() });
            toast.success(correcting ? "Sign-out time corrected." : "Signed out.");
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save.");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="onsite-signout-dialog" onClick={onClose}>
            <div className="bg-white rounded-[14px] w-full max-w-sm shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[14px] font-semibold flex-1">{correcting ? "Correct sign-out time" : "Sign out"} — {rec.name}</h3>
                    <button data-testid="onsite-signout-close" onClick={onClose}><X className="w-4 h-4 text-[#9A968F]" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <label className="block text-[12px] font-semibold text-[#1F1F1F]">Sign-out time
                        <input data-testid="onsite-signout-time" type="datetime-local" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full h-9 px-2.5 rounded-lg border text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                    </label>
                </div>
                <div className="px-5 py-3.5 border-t flex justify-end gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="onsite-signout-cancel" onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="onsite-signout-submit" onClick={submit} disabled={saving} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: BRAND }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Small "+ Sign someone in" trigger button — reused verbatim in both contexts.
export function SignInButton({ onClick, testid = "onsite-manual-signin-button" }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
            style={{ background: BRAND }}>
            <Plus size={14} /> Sign someone in
        </button>
    );
}
